'use client'

import React from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { CloseIcon } from '../Icon'
import {
  actionBarWrapperStyles,
  actionIconStyles,
  actionLabelStyles,
  actionSectionStyles,
  clearButtonStyles,
  selectionLabelStyles,
  selectionSectionStyles,
} from './ActionBar.styles'

export type ActionBarAction = {
  icon?: React.ReactNode
  label: string
  onClick: () => void
  isActive?: boolean
}

export type ActionBarProps = {
  selectionLabel: string
  onClearSelection: () => void
  actions: ActionBarAction[]
  /** colorScheme is accepted for API consistency but the ActionBar always
   *  renders in dark mode — it is a fixed chrome element of the dark design language. */
  colorScheme?: ColorScheme
}

export const ActionBar = ({
  selectionLabel,
  onClearSelection,
  actions,
  colorScheme = 'dark',
}: ActionBarProps) => {
  return (
    <Box {...actionBarWrapperStyles}>
      {/* Selection info */}
      <Box {...selectionSectionStyles(colorScheme)}>
        <Box as='span' {...selectionLabelStyles(colorScheme)}>
          {selectionLabel}
        </Box>
        <Box
          aria-label='Clear selection'
          as='button'
          onClick={onClearSelection}
          {...clearButtonStyles(colorScheme)}
        >
          <CloseIcon color='currentColor' size={12} />
        </Box>
      </Box>

      {/* Action buttons */}
      {actions.map((action, idx) => (
        <Box
          as='button'
          key={action.label}
          onClick={action.onClick}
          style={
            idx === actions.length - 1 ? { borderRight: 'none' } : undefined
          }
          {...actionSectionStyles(action.isActive, colorScheme)}
        >
          {action.icon && (
            <Box as='span' {...actionIconStyles}>
              {action.icon}
            </Box>
          )}
          <Box as='span' {...actionLabelStyles(colorScheme)}>
            {action.label}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
